import { capitalize } from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import {
  CustomThemeProvider,
  EmptyState,
  InputSearch,
  LoadingSkeleton,
  ModalUI,
  Switch,
  usePortal,
  useQueryFetch
} from 'sonata-react-components';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { DigitalAdExchangeCatalog, DOOHAdExchangeCatalog } from 'sonata-static-storage/lib/samples/adExchanges.samples';

import { getDeal } from '../../../services/assignDeals.service';
import { fetchBaseCurrency } from '../../../services/baseCurrency.service';

import './assignDealsModal.scss';

import { AssignDealSection } from '@/components/common/AssignDealSection/AssignDealSection';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { DealInterface } from 'sonata-static-storage/lib/schemas/premium/deal.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  theme?: 'dark' | 'light';
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

type State = {
  dealId?: string;
  foundDeal?: DealInterface<string>;
  notFoundDeal: boolean;
};

const DEAL_FIELDS: (keyof DealInterface<string>)[] = [
  'alias',
  'name',
  'origin',
  'seller',
  'type',
  'bidFloor',
  'endDate'
];

function getDealFieldValue(
  field: keyof DealInterface<string>,
  deal?: DealInterface<string>,
  currencyInfo?: BaseCurrencyInterface
): string | number {
  const dealFieldValue = field === 'origin' ? deal?.origin.name : deal?.[field];
  if (field === 'origin' && dealFieldValue) {
    return (
      [...Object.values(DigitalAdExchangeCatalog), ...Object.values(DOOHAdExchangeCatalog)].find(({ code }) => {
        return code === dealFieldValue;
      })?.name || capitalize(dealFieldValue as string)
    );
  }
  if (field === 'bidFloor' && dealFieldValue && currencyInfo) {
    return normalizeWithUnit(dealFieldValue as number, currencyInfo, 2);
  }
  if (field === 'endDate' && dealFieldValue) {
    return moment.utc(dealFieldValue as string).format('YYYY-MM-DD');
  }
  if (dealFieldValue) {
    return dealFieldValue as string;
  }
  return 'N/A';
}

function AssignDealsModal({ isOpen, onClose, theme = 'light', user, userRoles }: Props) {
  const { formatMessage } = useIntl();
  const tokens: Record<string, any> = {
    fetchBaseCurrency: {},
    getAssignedUsers: {},
    getDeal: {},
    getUsers: {}
  };
  const modalRef = useRef<React.ElementRef<typeof ModalUI>>(null);
  const [{ dealId, foundDeal, notFoundDeal }, setState] = useState<State>({
    notFoundDeal: false
  });
  const bidFloorPermission = checkUserResourcePermissions('dealBidFloor', user, userRoles);
  const baseId = 'assignDealsModal';

  const FILTERED_DEAL_FIELDS_BY_PERMISSIONS = DEAL_FIELDS.filter(field => {
    return field !== 'bidFloor' || bidFloorPermission;
  });

  const { isFetching: isSearchingDeals, refetch: searchDeal } = useQueryFetch(
    ['assignDeals', 'searchDeals', dealId],
    async () => {
      try {
        setState(prevState => ({
          ...prevState,
          notFoundDeal: false
        }));
        const newDeal = await getDeal(dealId!, tokens.getDeal);
        setState(prevState => ({
          ...prevState,
          foundDeal: newDeal || undefined,
          notFoundDeal: !newDeal
        }));
      } catch (_err) {
        setState(prevState => ({
          ...prevState,
          foundDeal: undefined,
          notFoundDeal: true
        }));
      }
    },
    {
      enabled: false
    }
  );

  const { data: dealCurrencyInfo, isFetching: isFetchingDealCurrencyInfo } = useQueryFetch(
    ['fetchDealBaseCurrency', foundDeal?._id],
    () => fetchBaseCurrency(foundDeal!.currencyCode, tokens.fetchBaseCurrency),
    {
      enabled: !!foundDeal && bidFloorPermission
    }
  );

  useEffect(() => {
    if (isOpen) {
      modalRef?.current?.openModal();
    } else {
      modalRef?.current?.closeModal();
    }
  }, [isOpen, modalRef]);

  const handleDealIdChange = (text: string) => {
    setState(prevState => ({
      ...prevState,
      dealId: text
    }));
  };

  const handleSearchDeal = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchDeal();
    }
  };

  const isLoading = isSearchingDeals || isFetchingDealCurrencyInfo;

  const renderDealDetailRows = (
    fields: (keyof DealInterface<string>)[],
    foundDeal: DealInterface<string> | undefined,
    dealCurrencyInfo: BaseCurrencyInterface | undefined,
    formatMessage: (msg: any) => string,
    capitalize: (s: string) => string
  ) => {
    const left: typeof fields = [];
    const right: typeof fields = [];
    fields.forEach((field, idx) => {
      (idx % 2 === 0 ? left : right).push(field);
    });
    const maxLen = Math.max(left.length, right.length);
    return Array.from({ length: maxLen }).map((_, rowIdx) => (
      <div className="assignDealModal__dealDetailRow" key={rowIdx}>
        {left[rowIdx] !== undefined ? (
          <div className="assignDealModal__dealDetailData">
            <div className="assignDealModal__dealDetailField alignLeft fontRegular">
              {`${formatMessage({
                defaultMessage: capitalize(left[rowIdx] as string),
                id: `assignDealsModal.dealDetails.fields.${left[rowIdx]}`
              })}:`}
            </div>
            <div className="assignDealModal__dealDetailValue" title={foundDeal?.[left[rowIdx]]?.toString()}>
              {getDealFieldValue(left[rowIdx], foundDeal, dealCurrencyInfo)}
            </div>
          </div>
        ) : (
          <div className="assignDealModal__dealDetailData assignDealModal__dealDetailData--empty" />
        )}
        {right[rowIdx] !== undefined ? (
          <div className="assignDealModal__dealDetailData">
            <div className="assignDealModal__dealDetailField alignLeft fontRegular">
              {`${formatMessage({
                defaultMessage: capitalize(right[rowIdx] as string),
                id: `assignDealsModal.dealDetails.fields.${right[rowIdx]}`
              })}:`}
            </div>
            <div className="assignDealModal__dealDetailValue" title={foundDeal?.[right[rowIdx]]?.toString()}>
              {getDealFieldValue(right[rowIdx], foundDeal, dealCurrencyInfo)}
            </div>
          </div>
        ) : (
          <div className="assignDealModal__dealDetailData assignDealModal__dealDetailData--empty" />
        )}
      </div>
    ));
  };

  const modal = ReactDOM.createPortal(
    <CustomThemeProvider themeContrast={theme}>
      <ModalUI
        className="assignDealModal"
        closeModalCallback={onClose}
        description={formatMessage({
          defaultMessage: 'Assign deals to specific users',
          id: 'menu.assignDealsModal.subtitle'
        })}
        id={baseId}
        ref={modalRef}
        title={formatMessage({
          defaultMessage: 'Assign Deals',
          id: 'menu.assignDealsModal.title'
        })}
        width="650px"
      >
        <div className="assignDealModal__dealSearchContainer">
          <InputSearch
            id={`${baseId}-deal-search-input`}
            lens
            name="dealId"
            onChange={handleDealIdChange as (val: string | FileList | null) => void}
            onKeyDown={handleSearchDeal}
            placeholder={formatMessage({
              defaultMessage: 'Type the deal ID',
              id: 'menu.assignDealsModal.dealIdPlaceholder'
            })}
            value={dealId}
          />
        </div>
        <Switch>
          <Switch.Case condition={isLoading}>
            <LoadingSkeleton count={1} height={120} />
          </Switch.Case>
          <Switch.Case condition={Boolean(foundDeal)}>
            <div className="assignDealModal__dealDetailHeader">
              <i className="fa-regular fa-tag assignDealModal__dealDetailHeaderIcon" />
              {getDealFieldValue('name', foundDeal, dealCurrencyInfo)}
            </div>
            <div className="assignDealModal__dealDetailContainer">
              <div className="assignDealModal__dealDetailGrid">
                {renderDealDetailRows(
                  FILTERED_DEAL_FIELDS_BY_PERMISSIONS,
                  foundDeal,
                  dealCurrencyInfo,
                  formatMessage,
                  capitalize
                )}
              </div>
            </div>
            {foundDeal && (
              <AssignDealSection
                className="assignDealModal__assignSection"
                deal={foundDeal}
                onSuccess={() =>
                  setState(prevState => ({
                    ...prevState,
                    foundDeal: undefined,
                    notFoundDeal: false
                  }))
                }
                user={user}
              />
            )}
          </Switch.Case>
          <Switch.Case condition={notFoundDeal}>
            <EmptyState
              description={formatMessage({
                defaultMessage: 'Type the ID number to search for deals',
                id: 'menu.assignDealsModal.notFoundDealDescription'
              })}
              icon="fal fa-empty-set"
              id={`notFoundDeal-${baseId}`}
              show
              title={formatMessage({
                defaultMessage: 'No deals found',
                id: 'menu.assignDealsModal.notFoundDeal'
              })}
            />
          </Switch.Case>
        </Switch>
      </ModalUI>
    </CustomThemeProvider>,
    usePortal('assignDealsModal')
  );

  if (!isOpen) {
    return null;
  }
  return modal;
}

export default AssignDealsModal;
